import type { QuizReviewBlock, QuizReviewItem } from "@/lib/quizReview";

type UserFeedback = { positive?: string; negative?: string } | undefined;

const pickFeedback = (
  feedback: UserFeedback | string,
  isCorrect: boolean
): string | undefined => {
  if (!feedback) return undefined;
  // LLM-graded quizzes pass their individual feedback text directly.
  if (typeof feedback === "string") {
    return feedback.trim() !== "" ? feedback : undefined;
  }
  const text = isCorrect ? feedback.positive : feedback.negative;
  return text && text.trim() !== "" ? text : undefined;
};

export const buildOptionsItem = (args: {
  prompt: string;
  codeSnippet?: string;
  options: { label: string; selected: boolean; correct: boolean }[];
  isCorrect: boolean;
  feedback?: UserFeedback;
}): QuizReviewItem => ({
  kind: "options",
  prompt: args.prompt,
  codeSnippet: args.codeSnippet,
  options: args.options,
  isCorrect: args.isCorrect,
  feedback: pickFeedback(args.feedback, args.isCorrect),
});

type ClozeGap = {
  userAnswer: string;
  correctAnswer: string;
  correct: boolean;
};

/**
 * A wrong question must not recap as only-success pills (e.g. 0/1 with one
 * green gap). Per-gap marks stay when the question is mixed.
 */
export const alignClozeGapsWithQuestion = (
  gaps: ClozeGap[],
  questionIsCorrect: boolean
): ClozeGap[] => {
  const wrongQuestionWouldShowOnlySuccessPills =
    !questionIsCorrect && gaps.length > 0 && gaps.every((gap) => gap.correct);
  if (!wrongQuestionWouldShowOnlySuccessPills) return gaps;
  return gaps.map((gap) => ({ ...gap, correct: false }));
};

export const buildClozeItem = (args: {
  prompt: string;
  clozeText: string;
  gapMark: string;
  gaps: ClozeGap[];
  isCorrect: boolean;
  feedback?: UserFeedback | string;
}): QuizReviewItem => ({
  kind: "cloze",
  prompt: args.prompt,
  clozeText: args.clozeText,
  gapMark: args.gapMark,
  gaps: alignClozeGapsWithQuestion(args.gaps, args.isCorrect),
  isCorrect: args.isCorrect,
  feedback: pickFeedback(args.feedback, args.isCorrect),
});

export const buildMatchingItem = (args: {
  prompt: string;
  rows: {
    secondPart: string;
    userTerm: string | null;
    correctTerm: string;
    correct: boolean;
  }[];
  isCorrect: boolean;
  feedback?: UserFeedback;
}): QuizReviewItem => ({
  kind: "matching",
  prompt: args.prompt,
  rows: args.rows,
  isCorrect: args.isCorrect,
  feedback: pickFeedback(args.feedback, args.isCorrect),
});

export const buildTextItem = (args: {
  prompt: string;
  userAnswer: string;
  isCorrect: boolean;
  feedback?: string;
}): QuizReviewItem => ({
  kind: "text",
  prompt: args.prompt,
  userAnswer: args.userAnswer,
  isCorrect: args.isCorrect,
  feedback:
    args.feedback && args.feedback.trim() !== "" ? args.feedback : undefined,
});

export const buildCodeItem = (args: {
  prompt: string;
  userAnswer: string;
  language?: string;
  isCorrect: boolean;
  feedback?: string;
}): QuizReviewItem => ({
  kind: "code",
  prompt: args.prompt,
  userAnswer: args.userAnswer,
  language: args.language,
  isCorrect: args.isCorrect,
  feedback:
    args.feedback && args.feedback.trim() !== "" ? args.feedback : undefined,
});

export const buildRevealItem = (args: {
  prompt: string;
  blocks: QuizReviewBlock[];
}): QuizReviewItem => ({
  kind: "reveal",
  prompt: args.prompt,
  blocks: args.blocks,
});
